// AIChat.jsx
import React, { useState, useRef, useEffect } from 'react';
import Lottie from 'lottie-react';
import ReactMarkdown from 'react-markdown';
import botCalm from '../../../assets/lottie/bot-calm.json';
import botThinking from '../../../assets/lottie/bot-thinking.json';
import './AIChat.css';

const AIChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, type: 'bot', text: '¡Hola! 👋\nSoy tu asistente virtual del Consorcio Advance\nEstoy lista para ayudarte con todo lo que necesites 😊\nCuéntame, ¿cuál es tu consulta el día de hoy?' }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamedText, setStreamedText] = useState('');
  const chatEndRef = useRef(null);
  const [threadId, setThreadId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' });
      }, 0);
    }
  }, [isOpen]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, streamedText]);

  // Componente de indicador de escritura
  const TypingIndicator = () => (
    <div className="flex justify-start mb-4">
      <div className="bg-white text-gray-600 px-4 py-3 rounded-2xl shadow-md" style={{animation: 'pulseGentle 2s ease-in-out infinite'}}>
        <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #dc2626, #fca5a5)',
              animation: 'typingDot 1.4s infinite ease-in-out'
            }}></div>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #dc2626, #fca5a5)',
              animation: 'typingDot 1.4s infinite ease-in-out',
              animationDelay: '0.2s'
            }}></div>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #dc2626, #fca5a5)',
              animation: 'typingDot 1.4s infinite ease-in-out',
              animationDelay: '0.4s'
            }}></div>
          </div>
          <span className="text-sm font-medium text-gray-500">Escribiendo...</span>
        </div>
      </div>
    </div>
  );

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = { id: Date.now(), type: 'user', text: inputMessage };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const res = await fetch('https://agente-atenea-api.azurewebsites.net/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: inputMessage,
          threadId: threadId // enviar el threadId actual si existe
        })
      });

      const data = await res.json();

      // Si el backend retorna un threadId (solo será nuevo la primera vez)
      if (data.threadId && !threadId) {
        setThreadId(data.threadId);
      }

      const cleanText = data.response?.replace(/【[^】]+】/g, '').trim() || 'Respuesta inválida.';

      setStreamedText('');
      let i = 0;
      const interval = setInterval(() => {
        setStreamedText(cleanText.slice(0, i));
        i++;
        if (i > cleanText.length) {
          clearInterval(interval);
          setMessages((prev) => [...prev, { id: Date.now(), type: 'bot', text: cleanText }]);
          setStreamedText('');
          setLoading(false);
        }
      }, 15);
    } catch (err) {
      setMessages((prev) => [...prev, {
        id: Date.now(),
        type: 'bot',
        text: '⚠️ Hubo un problema al conectar con el agente.'
      }]);
      setLoading(false);
    }
  };

  return (
    <>
      {/* BOTÓN ORIGINAL EXACTO - SIN MODIFICACIONES */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 group w-16 h-16 rounded-full bg-gradient-to-r from-red-600 to-red-700 flex items-center justify-center shadow-xl hover:scale-110 transition"
        >
          <Lottie animationData={botThinking} className="w-14 h-14" loop autoplay />
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[95%] max-w-2xl h-[600px] sm:h-[600px] bg-white rounded-3xl shadow-2xl flex flex-col animate-fadeInUp">
          <div className="relative h-0 hidden sm:block">
            <div className="absolute -top-28 -left-24 w-48 h-48 z-[60] pointer-events-none">
              <Lottie
                animationData={loading ? botThinking : botCalm}
                loop
                autoplay
              />
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-red-600 text-2xl font-bold z-[70] transition-colors duration-200"
            >
              ✕
            </button>
          </div>

          <div className="sm:hidden relative">
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-red-600 text-2xl font-bold z-50 transition-colors duration-200"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 pt-8 space-y-4 bg-gray-50">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-4 py-3 rounded-2xl text-base whitespace-pre-line transition-all duration-300 ${
                  msg.type === 'user' 
                    ? 'bg-gradient-to-r from-red-500 to-red-700 text-white shadow-lg' 
                    : 'bg-white text-gray-800 shadow-md hover:shadow-lg'
                }`} style={{fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'}}>
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                </div>
              </div>
            ))}

            {/* Indicador de escritura cuando está cargando pero no hay streaming */}
            {loading && !streamedText && <TypingIndicator />}

            {streamedText && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 px-4 py-3 rounded-2xl text-base animate-blur-in shadow max-w-[75%]" style={{fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'}}>
                  <ReactMarkdown>{streamedText}</ReactMarkdown>
                  <span style={{animation: 'blink 1s infinite', color: '#dc2626', fontWeight: 'bold', marginLeft: '2px'}}>|</span>
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          <div className="p-4 border-t bg-white">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !loading && sendMessage()}
                  placeholder="Escribe tu mensaje..."
                  className="flex-1 w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-red-400 transition-all duration-200"
                  style={{fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'}}
                  disabled={loading}
                />
                {loading && (
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #e5e7eb',
                      borderTop: '2px solid #dc2626',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                  </div>
                )}
              </div>
              <button
                onClick={sendMessage}
                disabled={loading || !inputMessage.trim()}
                className="px-6 py-3 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'}}
              >
                {loading ? (
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #ffffff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                ) : (
                  'Enviar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChat;